import React, { useState } from 'react';
import AlertModal from '../components/AlertModal';

interface SubscriptionPageProps {
  currentUser?: {
    id: string;
    name: string;
    email: string;
  };
}

const SubscriptionPage: React.FC<SubscriptionPageProps> = ({ currentUser }) => {
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'pro' | 'premium'>('free');
  const [alertModal, setAlertModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'info',
  });

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for getting started',
      features: [
        'Solo focus sessions',
        'Basic mind games',
        'Simple analytics',
        'Basic music options',
        'Email support'
      ],
      limitations: [
        'Limited to 2 hours per day',
        'Basic analytics only',
        'No team features'
      ],
      buttonText: 'Current Plan',
      buttonStyle: 'bg-gray-200 text-gray-600 cursor-not-allowed'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$9.99',
      period: 'per month',
      description: 'For serious focus enthusiasts',
      features: [
        'Unlimited solo sessions',
        'Advanced mind games',
        'Detailed analytics & reports',
        'Premium music library',
        'Custom session templates',
        'Priority support',
        'Export work logs',
        'Advanced focus techniques'
      ],
      limitations: [
        'No team collaboration',
        'Limited to 5 custom templates'
      ],
      buttonText: 'Upgrade to Pro',
      buttonStyle: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$19.99',
      period: 'per month',
      description: 'For teams and power users',
      features: [
        'Everything in Pro',
        'Team collaboration features',
        'Create & join focus rooms',
        'Shared team analytics',
        'Unlimited custom templates',
        'Advanced team management',
        'Screen sharing in sessions',
        'Team performance insights',
        'Custom team branding',
        'API access',
        '24/7 priority support'
      ],
      limitations: [],
      buttonText: 'Upgrade to Premium',
      buttonStyle: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
    }
  ];

  const handleUpgrade = (planId: string) => {
    if (planId === 'free') return;
    setAlertModal({
      open: true,
      title: 'Upgrade Plan',
      message: `Upgrading to ${planId.toUpperCase()} plan!`,
      type: 'info',
    });
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden">
      {/* Background gradient shapes */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-orange-300/40 to-orange-200/30 rounded-full blur-3xl"></div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
          <p className="text-gray-600 text-lg">Unlock the full potential of your focus sessions</p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border transition-all duration-300 shadow-lg ${
                plan.id === 'premium'
                  ? 'border-orange-400/70 shadow-xl shadow-orange-200 scale-105'
                  : 'border-orange-200/50 hover:border-orange-400/70 hover:shadow-xl'
              }`}
            >
              {plan.id === 'premium' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600 ml-2">{plan.period}</span>
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              <div className="space-y-4 mb-8">
                <h4 className="text-lg font-semibold text-gray-900">Features:</h4>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {plan.limitations.length > 0 && (
                <div className="space-y-2 mb-8">
                  <h4 className="text-lg font-semibold text-gray-900">Limitations:</h4>
                  <ul className="space-y-1">
                    {plan.limitations.map((limitation, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs">✗</span>
                        </div>
                        <span className="text-gray-500 text-sm">{limitation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => handleUpgrade(plan.id)}
                className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 ${
                  plan.id === 'premium' 
                    ? 'hover:shadow-lg hover:shadow-orange-200' 
                    : 'hover:shadow-lg'
                } ${plan.buttonStyle}`}
                disabled={plan.id === 'free'}
              >
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>

        {/* Feature Comparison */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-orange-200/50 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Feature Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-orange-200/50">
                  <th className="text-left py-4 text-gray-900 font-semibold">Features</th>
                  <th className="text-center py-4 text-gray-900 font-semibold">Free</th>
                  <th className="text-center py-4 text-gray-900 font-semibold">Pro</th>
                  <th className="text-center py-4 text-gray-900 font-semibold">Premium</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Solo Focus Sessions', free: '✓', pro: '✓', premium: '✓' },
                  { feature: 'Team Collaboration', free: '✗', pro: '✗', premium: '✓' },
                  { feature: 'Advanced Analytics', free: '✗', pro: '✓', premium: '✓' },
                  { feature: 'Premium Music Library', free: '✗', pro: '✓', premium: '✓' },
                  { feature: 'Custom Templates', free: '✗', pro: '5', premium: 'Unlimited' },
                  { feature: 'Export Work Logs', free: '✗', pro: '✓', premium: '✓' },
                  { feature: 'Screen Sharing', free: '✗', pro: '✗', premium: '✓' },
                  { feature: 'API Access', free: '✗', pro: '✗', premium: '✓' },
                  { feature: 'Priority Support', free: '✗', pro: '✓', premium: '24/7' }
                ].map((row, index) => (
                  <tr key={index} className="border-b border-orange-100/50">
                    <td className="py-4 text-gray-700">{row.feature}</td>
                    <td className="text-center py-4 text-gray-600">{row.free}</td>
                    <td className="text-center py-4 text-gray-600">{row.pro}</td>
                    <td className="text-center py-4 text-gray-600">{row.premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-orange-200/50 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h3>
          <div className="space-y-6">
            {[
              {
                question: "Can I change my plan anytime?",
                answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately."
              },
              {
                question: "What happens to my data if I cancel?",
                answer: "Your data is preserved for 30 days after cancellation. You can reactivate your account to restore access."
              },
              {
                question: "Do you offer team discounts?",
                answer: "Yes, we offer special pricing for teams of 10+ members. Contact our sales team for custom pricing."
              },
              {
                question: "Is there a free trial for Premium?",
                answer: "Yes, Premium plans come with a 14-day free trial. No credit card required."
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-orange-100/50 pb-4">
                <h4 className="text-gray-900 font-semibold mb-2">{faq.question}</h4>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Alert Modal */}
      <AlertModal
        open={alertModal.open}
        onClose={() => setAlertModal({ ...alertModal, open: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
};

export default SubscriptionPage;
